import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, StatusChip } from '@monigo/ui';
import { useTransfer, useCancelTransfer } from './api.js';
import { qk } from '../../lib/api/queryKeys.js';
import { supabase } from '../../lib/supabase.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';

function ArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export function TransferDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const transferQ = useTransfer(id);
  const qc = useQueryClient();
  const cancel = useCancelTransfer();

  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`transfer:${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'transfers', filter: `id=eq.${id}` },
        () => qc.invalidateQueries({ queryKey: qk.transfer(id) }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [id, qc]);

  if (transferQ.isLoading) return <LoadingScreen />;
  if (transferQ.isError || !transferQ.data) {
    return <ErrorState onRetry={() => transferQ.refetch()} />;
  }

  const { transfer, events } = transferQ.data;

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="flex items-center justify-between">
        <Link
          to="/transfers"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-surface-elevated text-text"
        >
          <ArrowLeft />
        </Link>
        <Link to="/transfers/new" className="text-xl font-semibold tracking-[0.08em] text-primary">
          {t('transfers.sendAgain')}
        </Link>
      </div>

      {transfer.status === 'PENDING_KYC' ? (
        <section className="rounded-[1.5rem] bg-warning/10 p-6 text-center">
          <p className="text-2xl font-medium text-text">{t('kyc.requiredTitle')}</p>
          <p className="mt-3 text-lg text-text-muted">{t('kyc.requiredDescription')}</p>
          <Link to="/kyc" className="mt-5 inline-block">
            <Button className="h-14 px-8 text-lg font-semibold">{t('kyc.submitCta')}</Button>
          </Link>
        </section>
      ) : null}

      <section className="space-y-6">
        <h1 className="text-3xl font-medium text-primary">{t('transfers.statusTitle')}</h1>
        <div className="border-t border-border pt-6">
          <ol className="space-y-0">
            {[
              'PENDING_KYC',
              'PENDING_PAYMENT',
              'PAYMENT_RECEIVED',
              'PROCESSING',
              'PAID_OUT',
              'COMPLETED',
            ].map((stepStatus, index) => {
              const event = events.find((e) => e.to_status === stepStatus);
              const statusIndex = events.findIndex((e) => e.to_status === transfer.status);

              let reachedIndex = -1;
              if (statusIndex >= 0) {
                const currentEvent = events[statusIndex];
                const currentStatus = currentEvent!.to_status;
                reachedIndex = [
                  'PENDING_KYC',
                  'PENDING_PAYMENT',
                  'PAYMENT_RECEIVED',
                  'PROCESSING',
                  'PAID_OUT',
                  'COMPLETED',
                ].indexOf(currentStatus);
              }

              const isDone = reachedIndex >= 0 && index < reachedIndex;
              const isActive = index === reachedIndex && reachedIndex >= 0;
              const isFuture = !isActive && !isDone;

              return (
                <li key={stepStatus} className="relative flex gap-5 pb-8 last:pb-0">
                  {index < 5 ? (
                    <span
                      className={`absolute left-[1.1rem] top-10 w-0.5 ${
                        isDone || isActive ? 'bg-success' : 'bg-border'
                      }`}
                      style={{ height: 'calc(100% - 1rem)' }}
                    />
                  ) : null}

                  <span
                    className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold ${
                      isDone
                        ? 'bg-success text-white'
                        : isActive
                          ? 'border-2 border-primary bg-white text-primary ring-2 ring-primary/30'
                          : 'border-2 border-border bg-white text-text-muted'
                    }`}
                  >
                    {isDone ? '✓' : index + 1}
                  </span>

                  <div className="min-w-0 pt-1.5">
                    <p
                      className={`text-xl font-medium ${
                        isFuture ? 'text-text-muted' : 'text-text'
                      }`}
                    >
                      {t(`transfers.status.${stepStatus}`)}
                    </p>
                    {event ? (
                      <p className="mt-0.5 text-base text-text-muted">
                        {new Date(event.created_at).toLocaleDateString(
                          i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                          { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
                        )}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="space-y-6 border-t border-border pt-8">
        <h2 className="text-3xl font-medium text-primary">{t('transfers.recipientTitle')}</h2>
        <div className="flex items-center gap-5 border-b border-border pb-8">
          <div className="flex h-16 w-20 items-center justify-center rounded-[1rem] border border-border bg-surface-elevated p-4">
            {transfer.beneficiary_payout_method === 'bank' ? (
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="w-full h-full"
              >
                <rect
                  x="2"
                  y="8"
                  width="20"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M12 2L4 8H20L12 2Z" fill="currentColor" />
                <line x1="8" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            ) : (
              <img
                src={
                  (transfer.beneficiary_payout_details as Record<string, unknown> | null)
                    ?.provider === 'orange_money'
                    ? '/ORANGE_ICON.png'
                    : '/MTN_ICON.png'
                }
                alt=""
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div>
            <p className="text-2xl font-medium text-text">
              {transfer.beneficiary_name ?? transfer.reference_code}
            </p>
            {transfer.beneficiary_payout_details ? (
              <p className="text-lg text-text-muted mt-1">
                {transfer.beneficiary_payout_method === 'bank'
                  ? String(transfer.beneficiary_payout_details.iban ?? '')
                  : String(transfer.beneficiary_payout_details.phone_number ?? '')}
              </p>
            ) : null}
          </div>
        </div>

        <h2 className="text-3xl font-medium text-primary">{t('transfers.amountTitle')}</h2>
        <div className="space-y-4 border-b border-border pb-8 text-xl text-text-muted">
          <Row
            label={t('transfers.sentAmount')}
            value={`${transfer.amount_send} ${transfer.from_currency}`}
          />
          <Row
            label={t('transfers.feeLabel')}
            value={`${transfer.fee} ${transfer.from_currency}`}
          />
          {(transfer.promocode_discount_applied ?? 0) > 0 ? (
            <Row
              label={t('transfers.discountLabel')}
              value={`-${(transfer.promocode_discount_applied ?? 0)} ${transfer.from_currency}`}
            />
          ) : null}
          <Row
            label={t('transfers.totalToRecipient')}
            value={`${transfer.amount_receive} ${transfer.to_currency}`}
            strong
          />
        </div>

        <h2 className="text-3xl font-medium text-primary">{t('transfers.detailsTitle')}</h2>
        <div className="space-y-4 text-xl text-text-muted">
          <div className="flex items-start justify-between gap-4">
            <span>{t('transfers.initiatedAt')}</span>
            <div className="flex items-center gap-3">
              <span>
                {new Date(transfer.created_at).toLocaleDateString(
                  i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  },
                )}
              </span>
              <StatusChip status={transfer.status} />
            </div>
          </div>
          <Row label={t('transfers.transferIdLabel')} value={transfer.reference_code} />
          <Row
            label={t('transfers.rateLabel')}
            value={`1 ${transfer.from_currency} = ${transfer.exchange_rate} ${transfer.to_currency}`}
          />
        </div>

        {transfer.status === 'PENDING_PAYMENT' ? (
          <div className="space-y-4 pt-6">
            <Link to={`/payment/${transfer.id}`} className="block">
              <Button className="h-16 w-full text-2xl font-medium">
                {t('transfers.payManuallyCta')}
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => cancel.mutate(transfer.id)}
              className="h-14 w-full text-xl font-medium"
            >
              {t('transfers.cancelCta')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-6">
            <Button className="h-16 w-full text-2xl font-medium">
              {t('transfers.downloadReceiptCta')}
            </Button>
            <Link to="/support" className="block text-center text-2xl font-medium text-primary">
              {t('transfers.getHelpCta')}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className={strong ? 'font-semibold text-text' : ''}>{label}</span>
      <span className={strong ? 'font-semibold text-text' : ''}>{value}</span>
    </div>
  );
}
